<!--
 * Path: packages/frontend/src/core/components/dialogs/BaseDialog.vue
 * Task: Reusable core dialog skeleton natively based on Vuetify 3 components.
 * Update: Enforced strict flexbox heights (flex-shrink-0) to prevent custom elements like BaseTabbar from being pushed out of view by v-card-text.
-->
<template>
  <v-dialog
    v-model="modelValue"
    :max-width="maxWidth"
    scrollable
  >
    <v-card class="position-relative d-flex flex-column" style="max-height: 90vh;">
      <!-- Native Vuetify card title serving as the header (Only if title is provided) -->
      <v-card-title v-if="title" class="d-flex justify-space-between py-4 px-6 align-center flex-shrink-0">
        <span class="text-h7">
          {{ title }}
        </span>
        <v-btn
          icon
          variant="text"
          density="comfortable"
          color="medium-emphasis"
          @click="$emit('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Absolute floating close button when no standard title is used -->
      <v-btn
        v-else
        icon
        variant="text"
        density="comfortable"
        color="medium-emphasis"
        class="position-absolute"
        style="top: 8px; right: 8px; z-index: 10;"
        @click="$emit('close')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <!-- Modular tab bar injecting custom tab slots (flex-shrink-0 ensures it never scrolls away) -->
      <BaseTabbar
        v-if="$slots.tabs"
        v-model="tabModel"
        class="flex-shrink-0 px-2"
      >
        <slot name="tabs"></slot>
      </BaseTabbar>

      <!-- Main scrollable content area of the dialog -->
      <v-card-text class="pa-6 flex-grow-1 overflow-y-auto">
        <slot></slot>
      </v-card-text>

      <v-divider v-if="$slots.actions" class="flex-shrink-0"></v-divider>

      <!-- Standard actions footer of the dialog -->
      <v-card-actions v-if="$slots.actions" class="pa-4 flex-shrink-0">
        <slot name="actions"></slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { watch, onUnmounted } from 'vue';
import BaseTabbar from '@/core/components/common/baseTab/BaseTabbar.vue';

interface Props {
  title?: string;
  maxWidth?: string | number;
}

withDefaults(defineProps<Props>(), {
  title: '',
  maxWidth: 500
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const modelValue = defineModel<boolean>({ default: false });
const tabModel = defineModel<string | number | undefined>('tabModel');

// Keyboard event listener to close the dialog when Escape key is pressed
const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && modelValue.value) {
    emit('close');
  }
};

// Monitor visibility state to register/unregister the keyboard event listener
watch(modelValue, (isActive) => {
  if (isActive) {
    window.addEventListener('keydown', handleKeyDown);
  } else {
    window.removeEventListener('keydown', handleKeyDown);
  }
}, { immediate: true });

// Cleanup the event listener when the component is unmounted
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
});
</script>
